/**
 * Typed bridge to the Tauri IPC commands defined in `desktop/src-tauri/src/ipc.rs`.
 *
 * Phase D (2026-05-19) of the extraction-remediation plan. The SPA calls these
 * via `@tauri-apps/api/core`'s `invoke()`. We re-export typed wrappers so
 * callers don't litter string literals + Rust-style `snake_case` command names
 * across the React tree.
 *
 * Every wrapper does a lazy import of `@tauri-apps/api/core` so the web build
 * doesn't pull the Tauri JS into its bundle. Only the desktop build's webview
 * actually loads these modules (the dynamic import returns a no-op-shaped
 * rejection in the web build, which the wrapper translates into a clean
 * "desktop-only" error).
 */

import { isDesktopApp } from './isDesktopApp';

/** Shape returned by `get_token_status` — has the keychain a token yet? */
export interface TokenStatus {
  has_token: boolean;
  /** First-4 + last-4 form. NEVER the raw token. */
  masked: string | null;
}

/** Shape returned by `configure_claude_desktop`. */
export interface ConfigureClaudeResult {
  ok: boolean;
  /** Path the wrapper wrote (or attempted to write). */
  path: string | null;
  /** Human-readable status — render verbatim. */
  message: string;
}

/**
 * Lazy load of `@tauri-apps/api/core`. Returns the `invoke` function or throws
 * a "desktop-only" error in the web build.
 */
async function getInvoke(): Promise<<T,>(cmd: string, args?: Record<string, unknown>) => Promise<T>> {
  if (!isDesktopApp()) {
    throw new Error('desktop-only IPC called from web build');
  }
  // Dynamic import — the web build's tree-shaker drops this branch since the
  // call site is guarded by `isDesktopApp()`.
  const mod = await import('@tauri-apps/api/core');
  return mod.invoke as <T>(cmd: string, args?: Record<string, unknown>) => Promise<T>;
}

/**
 * Copy a PNG image to the system clipboard via the NATIVE (Rust) clipboard.
 *
 * WebView2's `navigator.clipboard.write([ClipboardItem image/png])` silently
 * drops image bytes, so the editor's "Copy image" routes here on the desktop
 * build (and falls back to the web Clipboard API on the web build). Bytes are
 * sent as a plain number array so they deserialize cleanly to Rust `Vec<u8>`.
 */
export async function copyImagePng(png: Uint8Array): Promise<void> {
  const invoke = await getInvoke();
  await invoke<void>('copy_image_png', { png: Array.from(png) });
}

/**
 * Persist a freshly-minted token to the OS keychain. Call this RIGHT after
 * the cloud backend's `/system/mcp_token/mint` returns — the plaintext is
 * returned exactly once and must not be cached anywhere else.
 */
export async function storeMcpToken(workspaceId: string, token: string): Promise<boolean> {
  const invoke = await getInvoke();
  return invoke<boolean>('mint_and_store_token', { workspaceId, token });
}

/** Has the keychain a token, and what's the display masked form? */
export async function getTokenStatus(workspaceId: string): Promise<TokenStatus> {
  const invoke = await getInvoke();
  return invoke<TokenStatus>('get_token_status', { workspaceId });
}

/**
 * REVEAL the plaintext token — for the Settings "Reveal" button. The caller
 * must auto-hide after a short timeout so a screenshot doesn't capture it.
 */
export async function revealMcpToken(workspaceId: string): Promise<string | null> {
  const invoke = await getInvoke();
  return invoke<string | null>('reveal_token', { workspaceId });
}

/** Wipe the keychain entry. Pair with the cloud-side revoke endpoint. */
export async function deleteMcpToken(workspaceId: string): Promise<boolean> {
  const invoke = await getInvoke();
  return invoke<boolean>('delete_token', { workspaceId });
}

/** Absolute path of the running aletheia.exe (for the config-snippet view). */
export async function getInstallPath(): Promise<string> {
  const invoke = await getInvoke();
  return invoke<string>('get_install_path');
}

/** Local HTTP MCP URL the in-app server is bound to. */
export async function getLocalMcpUrl(): Promise<string> {
  const invoke = await getInvoke();
  return invoke<string>('get_local_mcp_url');
}

/**
 * One-click "Configure Claude Desktop" — the IPC writes Aletheia's entry
 * into `%APPDATA%\Claude\claude_desktop_config.json` atomically, preserving
 * any other MCP servers already configured.
 */
export async function configureClaudeDesktop(workspaceId: string): Promise<ConfigureClaudeResult> {
  const invoke = await getInvoke();
  return invoke<ConfigureClaudeResult>('configure_claude_desktop', { workspaceId });
}

/**
 * Open the webview DevTools (inspector) for the main window. Desktop-only — the
 * chromeless window has no menu or F12 affordance, so Settings exposes this so a
 * styling / stale-cache issue can be inspected (and the cache cleared) from inside
 * the app. No-ops/throws in the web build (guarded by `isDesktopApp()`).
 */
export async function openDevtools(): Promise<void> {
  const invoke = await getInvoke();
  await invoke<void>('open_devtools');
}

/**
 * Minimize / maximize-toggle / close / drag, for the custom title-bar.
 *
 * These use the Tauri v2 JS window API (`getCurrentWindow().minimize()`, etc.)
 * — NOT custom Rust IPC commands. The desktop window loads the production SWA
 * (a REMOTE origin), and a remote origin's `invoke()` is permission-gated
 * against PLUGIN permissions; it cannot reach arbitrary app-defined commands
 * like `window_minimize`. The `core:window:*` permissions these JS calls map to
 * ARE granted in `capabilities/default.json` (allow-minimize / -toggle-maximize
 * / -close / -start-dragging), so this path works from the remote SPA — the same
 * approach already proven by `windowStartResize` below.
 *
 * (History: v0.1.5 used the v1 `appWindow` export, which was REMOVED in Tauri v2
 * — so it was `undefined` and silently no-op'd, misdiagnosed as tree-shaking.
 * The v0.1.6 "custom Rust command" workaround then failed from the remote origin.
 * `getCurrentWindow()` is the correct v2 API.)
 */
async function currentWindow(): Promise<{
  minimize(): Promise<void>;
  toggleMaximize(): Promise<void>;
  close(): Promise<void>;
  startDragging(): Promise<void>;
}> {
  const mod = await import('@tauri-apps/api/window');
  return mod.getCurrentWindow();
}

export async function windowMinimize(): Promise<void> {
  if (!isDesktopApp()) return;
  try {
    await (await currentWindow()).minimize();
  } catch {
    /* not in Tauri / API unavailable — no-op in the browser build */
  }
}

export async function windowToggleMaximize(): Promise<void> {
  if (!isDesktopApp()) return;
  try {
    await (await currentWindow()).toggleMaximize();
  } catch {
    /* no-op outside Tauri */
  }
}

export async function windowClose(): Promise<void> {
  if (!isDesktopApp()) return;
  try {
    await (await currentWindow()).close();
  } catch {
    /* no-op outside Tauri */
  }
}

/** Start an OS-level window drag — hooked to `mousedown` on the TopBar's drag surface. */
export async function windowStartDragging(): Promise<void> {
  if (!isDesktopApp()) return;
  try {
    await (await currentWindow()).startDragging();
  } catch {
    /* no-op outside Tauri */
  }
}

/** The eight OS resize directions (mirror Tauri's `ResizeDirection`). */
export type ResizeDirection =
  | 'North'
  | 'South'
  | 'East'
  | 'West'
  | 'NorthEast'
  | 'NorthWest'
  | 'SouthEast'
  | 'SouthWest';

/**
 * Start an OS-level window resize from a custom grip's `mousedown`. Needed
 * because `decorations: false` strips the native resize border on Windows —
 * the SPA renders its own edge/corner grips (see `<WindowResizeGrips>`).
 *
 * Uses the JS window API (`getCurrentWindow().startResizeDragging`) rather than
 * a custom Rust command: the `ResizeDirection` enum's module path differs
 * across Tauri 2.x minors, and the JS API is gated by the same
 * `core:window:allow-start-resize-dragging` permission. The direction strings
 * ('North', 'SouthEast', …) match the JS `ResizeDirection` enum values.
 */
export async function windowStartResize(direction: ResizeDirection): Promise<void> {
  if (!isDesktopApp()) return;
  try {
    const mod = await import('@tauri-apps/api/window');
    // The JS enum values are the same PascalCase strings; cast through unknown
    // so we don't need to import the enum type (keeps the web build clean).
    await mod.getCurrentWindow().startResizeDragging(direction as unknown as never);
  } catch {
    /* not in Tauri / API unavailable — no-op in the browser build */
  }
}

/**
 * Quit the entire Aletheia desktop app — wired to the "Exit" entry in the
 * TopBar burger menu. Distinct from `windowClose()` which only closes the
 * active window; this triggers Rust's `app.exit(0)` so the process tree
 * shuts down cleanly (the local MCP HTTP server, any pending tokio tasks).
 */
export async function appExit(): Promise<void> {
  const invoke = await getInvoke();
  await invoke<void>('app_exit');
}
